/**
 * React Query hooks for search-related API endpoints
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { dataApiClient } from '../client'
import { staticCacheConfig, createCacheKey } from '../../../lib/utils'
import type { SearchRequest } from '../'

/**
 * Hook to perform search and get filter set
 */
export function useSearch(
  request: SearchRequest,
  options?: {
    enabled?: boolean
    staleTime?: number
    gcTime?: number
  }
) {
  return useQuery({
    queryKey: createCacheKey('search', request),
    queryFn: async () => {
      const { data, error } = await dataApiClient.POST('/search', {
        body: request,
      })

      if (error) {
        throw new Error(`Failed to perform search: ${error}`)
      }

      return data
    },
    enabled: options?.enabled ?? true,
    staleTime: options?.staleTime ?? staticCacheConfig.staleTime,
    gcTime: options?.gcTime ?? staticCacheConfig.gcTime,
  })
}

/**
 * Hook to prefetch search results
 */
export function usePrefetchSearch() {
  const queryClient = useQueryClient()

  return (request: SearchRequest) => {
    queryClient.prefetchQuery({
      queryKey: createCacheKey('search', request),
      queryFn: async () => {
        const { data, error } = await dataApiClient.POST('/search', {
          body: request,
        })

        if (error) {
          throw new Error(`Failed to prefetch search: ${error}`)
        }

        return data
      },
      staleTime: staticCacheConfig.staleTime,
    })
  }
}

/**
 * Utility hook to invalidate search cache
 */
export function useInvalidateSearch() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: ['search'],
    })
  }
}
